/**
 * ExportOptionsCheckboxes - Option toggles for an export modal
 *
 * Renders only the options the selected format supports, in the caller's configured order.
 */
import type { ReactElement } from 'react';

export interface ExportOptionConfig<K extends string> {
  key: K;
  label: string;
}

export interface ExportOptionsCheckboxesProps<K extends string> {
  /** Display order and labels for every option this modal can show */
  config: ExportOptionConfig<K>[];
  /** Options the selected format supports; anything else is hidden */
  supportedOptions: K[];
  values: Record<K, boolean>;
  onChange: (key: K, value: boolean) => void;
}

export default function ExportOptionsCheckboxes<K extends string>({
  config,
  supportedOptions,
  values,
  onChange,
}: ExportOptionsCheckboxesProps<K>): ReactElement | null {
  const visible = config.filter(({ key }) => supportedOptions.includes(key));

  if (visible.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-4">
      {visible.map(({ key, label }) => (
        <label key={key} className="text-ash flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={values[key]}
            onChange={(e) => onChange(key, e.target.checked)}
            className="text-aether focus:ring-aether border-storm bg-slate h-4 w-4 rounded"
          />
          {label}
        </label>
      ))}
    </div>
  );
}
